import React, { forwardRef } from 'react';
import styled, { css } from 'styled-components';
import { Icon, IconProps, IconSize } from '@ardoq/icons';
import { colors } from '@ardoq/design-tokens';
import { getFocusRingBorderCSS } from '@ardoq/style-helpers';
import { VisuallyHidden } from '@react-aria/visually-hidden';
import { SettingsBarPopover } from './SettingsBarPopover';

const CustomButton = styled.button<{
  $isDisabled?: boolean;
  $isActive?: boolean;
  $hasPopover?: boolean;
}>`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 36px;
  height: 36px;
  padding: 6px;
  border-radius: 4px;
  border: none;
  background-color: transparent;

  &:focus-visible {
    ${getFocusRingBorderCSS()};
  }

  ${({ $isActive }) =>
    $isActive &&
    css`
      background-color: ${colors.surfaceActionGhostPressed};
    `}

  ${({ $isDisabled, $hasPopover }) =>
    $isDisabled
      ? $hasPopover
        ? css`
            cursor: default;
          `
        : css`
            pointer-events: none;
            &[data-tooltip-text] {
              cursor: revert;
            }
          `
      : css`
          &:hover {
            background-color: ${colors.surfaceActionGhostHover};
          }
          &:active {
            background-color: ${colors.surfaceActionGhostPressed};
          }
        `}
`;

type ButtonIconProperties = Omit<IconProps, 'color'> & {
  popoverText?: string;
  isDisabled: boolean;
  isActive?: boolean;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  /**
   * The button is rendered as an icon only, the label is for accessibility purposes
   */
  label: string;
};

const getIconColor = (isActive?: boolean, isDisabled?: boolean) => {
  if (isDisabled) {
    return colors.iconActionDisabled;
  }
  if (isActive) {
    return colors.iconActionActive;
  }
  return colors.icon;
};

const ButtonIcon = forwardRef<HTMLButtonElement, ButtonIconProperties>(
  (
    {
      popoverText,
      isDisabled,
      isActive,
      onClick,
      label,
      ...iconProps
    }: ButtonIconProperties,
    ref
  ) => (
    <SettingsBarPopover popoverText={popoverText}>
      <CustomButton
        ref={ref}
        onClick={!isDisabled ? onClick : undefined}
        $isDisabled={isDisabled}
        disabled={isDisabled}
        $isActive={isActive}
        $hasPopover={Boolean(popoverText)}
        aria-label={label}
        {...(isActive !== undefined ? { 'aria-pressed': isActive } : {})}
      >
        <Icon
          {...iconProps}
          color={getIconColor(isActive, isDisabled)}
          iconSize={IconSize.MEDIUM}
        />
        <VisuallyHidden>{label}</VisuallyHidden>
      </CustomButton>
    </SettingsBarPopover>
  )
);

export { ButtonIcon };
